import React, { Component } from "react";
import "./CasePerformanceList.css";

const CATEGORIES = [
  { id: "1", label: "Pediatrics" },
  { id: "2", label: "Internal Medicine" },
  { id: "3", label: "OB/GYN" },
  { id: "4", label: "Neurology" },
  { id: "5", label: "Emergency Medicine" },
  { id: "6", label: "Psychiatry" },
  { id: "7", label: "Osteopathic" },
];

const SCORE_MODES = [
  {
    key: "first",
    label: "First Attempt",
    gradeKey: "FirstCaseAttemptGrade",
    avgKey: "AverageGeneralUserFirstCaseAttempt",
    avgLabel: "Users' 1st Avg",
  },
  {
    key: "best",
    label: "Best Attempt",
    gradeKey: "BestCaseAttemptGrade",
    avgKey: "AverageGeneralUserFirstCaseAttempt",
    avgLabel: "Users' 1st Avg",
  },
  {
    key: "avg",
    label: "Average",
    gradeKey: "AverageCaseAttemptGrade",
    avgKey: "AverageGeneralUserFirstCaseAttempt",
    avgLabel: "Users' 1st Avg",
  },
];

export default class CasePerformanceList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeCategories: new Set(CATEGORIES.map((c) => c.id)),
      scoreMode: "first",
      sortBy: "caseId", // "caseId" | "userScore" | "diff"
      sortDir: "asc",
    };
  }

  toggleCategory = (id) => {
    this.setState((prev) => {
      const next = new Set(prev.activeCategories);
      if (next.has(id)) {
        if (next.size === 1) return null; // keep at least one
        next.delete(id);
      } else {
        next.add(id);
      }
      return { activeCategories: next };
    });
  };

  setSort = (col) => {
    this.setState((prev) => ({
      sortBy: col,
      sortDir: prev.sortBy === col && prev.sortDir === "asc" ? "desc" : "asc",
    }));
  };

  render() {
    const { jsonreport } = this.props;
    const { activeCategories, scoreMode, sortBy, sortDir } = this.state;

    if (!jsonreport) return null;

    const mode = SCORE_MODES.find((m) => m.key === scoreMode);

    // Filter cases by active categories
    const filtered = jsonreport.filter((c) =>
      activeCategories.has(String(c.CaseCategory)),
    );

    // Build rows with computed values
    const rows = filtered.map((c) => {
      const userPct = parseFloat(c[mode.gradeKey]);
      const avgPct = parseFloat(c[mode.avgKey]);
      const diff = userPct - avgPct;
      return { ...c, userPct, avgPct, diff };
    });

    // Sort
    rows.sort((a, b) => {
      let va, vb;
      if (sortBy === "caseId") {
        va = a.CaseId;
        vb = b.CaseId;
      }
      if (sortBy === "userScore") {
        va = a.userPct;
        vb = b.userPct;
      }
      if (sortBy === "diff") {
        va = a.diff;
        vb = b.diff;
      }
      return sortDir === "asc" ? va - vb : vb - va;
    });

    // Summary
    const summaryUserAvg = rows.length
      ? rows.reduce((sum, r) => sum + r.userPct, 0) / rows.length
      : 0;
    const summaryAllAvg = rows.length
      ? rows.reduce((sum, r) => sum + r.avgPct, 0) / rows.length
      : 0;

    const sortIcon = (col) => {
      if (sortBy !== col) return " ↕";
      return sortDir === "asc" ? " ↑" : " ↓";
    };

    return (
      <div className="reportCardModuleContainer cplModule">
        <div className="reportCardModuleHeaderContainerflex">
          <p className="reportCardModuleHeaderText">Case Performance List</p>
          <div className="cplModeTabs">
            {SCORE_MODES.map((m) => (
              <button
                key={m.key}
                className={`cplModeTab ${scoreMode === m.key ? "cplModeTabActive" : ""}`}
                onClick={() => this.setState({ scoreMode: m.key })}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        <div className="reportCardModuleBodyContainer cplBody">
          {/* Category filter tags */}
          <div className="cplFilters">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                className={`cplFilterTag ${activeCategories.has(cat.id) ? "cplFilterTagActive" : ""}`}
                onClick={() => this.toggleCategory(cat.id)}
              >
                {activeCategories.has(cat.id) && (
                  <span className="cplFilterRemove">×</span>
                )}
                {cat.label}
              </button>
            ))}
          </div>

          {/* Summary bar */}
          <div className="cplSummary">
            <div className="cplSummaryItem">
              <span className="cplSummaryLabel">Your Overall Average</span>
              <span className="cplSummaryValue cplUser">
                {summaryUserAvg.toFixed(1)}%
              </span>
            </div>
            <div className="cplSummaryItem">
              <span className="cplSummaryLabel">
                All Users' Overall Average
              </span>
              <span className="cplSummaryValue cplAverage">
                {summaryAllAvg.toFixed(1)}%
              </span>
            </div>
          </div>

          {/* List header */}
          <div className="cplListHeader">
            <button
              className="cplHeaderBtn"
              onClick={() => this.setSort("caseId")}
            >
              Case #{sortIcon("caseId")}
            </button>
            <div>Diagnosis</div>
            <button
              className="cplHeaderBtn"
              onClick={() => this.setSort("userScore")}
            >
              Your Score{sortIcon("userScore")}
            </button>
            <div>{mode.avgLabel}</div>
            <button
              className="cplHeaderBtn"
              onClick={() => this.setSort("diff")}
            >
              Difference{sortIcon("diff")}
            </button>
            <div>Performance</div>
          </div>

          {/* Rows */}
          {rows.length === 0 && (
            <p className="cplEmpty">No cases match the selected filters.</p>
          )}
          {rows.map((row) => {
            const diffClass =
              row.diff > 0
                ? "cplAbove"
                : row.diff < 0
                  ? "cplBelow"
                  : "cplEqual";
            const diffSign = row.diff > 0 ? "+" : "";
            return (
              <div key={row.CaseId} className="cplRow">
                <div className="cplCaseNumber">Case {row.CaseId}</div>
                <div className="cplDiagnosis">{row.CaseDignosis}</div>
                <div className="cplScore cplUser">
                  {row.userPct.toFixed(1)}%
                </div>
                <div className="cplScore cplAverage">
                  {row.avgPct.toFixed(1)}%
                </div>
                <div className={`cplDifference ${diffClass}`}>
                  {diffSign}
                  {row.diff.toFixed(1)}%
                </div>
                <div className="cplBarWrap">
                  <div className="cplBar">
                    <div
                      className="cplBarFill"
                      style={{ width: `${Math.min(row.userPct, 100)}%` }}
                    />
                  </div>
                </div>
              </div>
            );
          })}

          {/* Legend */}
          <div className="cplLegend">
            <div className="cplLegendItem">
              <div className="cplLegendColor cplUser" />
              <span>Your Score</span>
            </div>
            <div className="cplLegendItem">
              <div className="cplLegendColor cplAverage" />
              <span>All Users' Average Score</span>
            </div>
          </div>
        </div>
      </div>
    );
  }
}